import { DataSourceExecutionEvent, ClickEvent, Layout, CrudDecorator, DataSourceMode, CrudDecoratorCloseEvent, TableRow, Panel, TableRowDisplayEvent, TableRowMoveEvent, DataSource, TableContentsChangedEvent, DataHeaderProps, TableRowMode } from "@mcleod/components";
import { Api, ModelRow } from "@mcleod/core";
import { ReflectiveDialogs } from "@mcleod/components/src/base/ReflectiveDialogs";
import { AutogenLayoutEdiReqdFldGrpExpandedStatus } from "./autogen/AutogenLayoutEdiReqdFldGrpExpandedStatus";
import { CrudDecoratorProps } from "@mcleod/components/src/page/decorators/CrudDecoratorProps";
import { EdiRequiredFieldStatus } from "./EdiRequiredFieldStatus";

export class EdiReqdFldGrpExpandedStatus extends AutogenLayoutEdiReqdFldGrpExpandedStatus {
    private _version: string;
    private _partnerId: string;
    private _altPartnerId: string;
    private _groupId: string;
    private _groupRow: ModelRow;
    private _parentDataSource: DataSource;
    private _needsSaving: boolean = false;
    private _transactionType: string;

    public get transactionType(): string {
        return this._transactionType;
    }
    public set transactionType(value: string) {
        this._transactionType = value;
    }

    public get needsSaving(): boolean {
        return this._needsSaving;
    }
    public set needsSaving(value: boolean) {
        if (value) {
            this.syncRequiredFieldsToParent();
            this.parentDataSource?.notifyHasChangedComponents(true);
        }
        this._needsSaving = value;
    }

    private syncRequiredFieldsToParent() {
        if (!this.parentDataSource) return;
        const groupRow = this._groupRow
            ?? this.parentDataSource.data?.find((row: ModelRow) => row.get("id") === this.groupId);
        if (groupRow) {
            const reqFields = this.tableEdiRequiredField.allRows.map(row => row.data);
            groupRow.set("required_fields", reqFields);
        }
    }

    public get parentDataSource(): DataSource {
        return this._parentDataSource;
    }
    public set parentDataSource(value: DataSource) {
        this._parentDataSource = value;
    }

    public get version(): string {
        return this._version;
    }

    public set version(value: string) {
        this._version = value;
    }

    public get partnerId(): string {
        return this._partnerId;
    }

    public set partnerId(value: string) {
        this._partnerId = value;
    }

    public get altPartnerId(): string {
        return this._altPartnerId;
    }

    public set altPartnerId(value: string) {
        this._altPartnerId = value;
    }

    public get groupId(): string {
        return this._groupId;
    }

    public set groupId(value: string) {
        this._groupId = value;
    }

    public get groupRow(): ModelRow {
        return this._groupRow;
    }

    public set groupRow(value: ModelRow) {
        this._groupRow = value;
    }

    override onLoad() {
        this.tableEdiRequiredField.onEditLayoutLoaded = (decorator, tableRow) => {
            const reqRulesLayout = decorator.layout as EdiRequiredFieldStatus;
            reqRulesLayout.version = this.version;
            reqRulesLayout.transactionType = this.transactionType;
            reqRulesLayout.isGroupRule = true;
            reqRulesLayout.isCreationRule = true;
            const superOnSave = decorator.onSave;
            const title = "Status Creation Conditional Rules";
            decorator.labelTitle.text = title;
            decorator.onSave = async (updatedRow) => {
                if (decorator.layout.mainDataSource.validate()) {
                    const rules = reqRulesLayout.tableEdiDataRule?.allRows.map((row) => row.data) || [];
                    updatedRow.set('data_rules', rules);
                    const rowToSend = updatedRow?.data ? updatedRow.data : updatedRow;
                    const response = await Api.post("lme/datafusion/set-rules-description", { required_fields: rowToSend, version: reqRulesLayout.version });
                    updatedRow.set('rule_description', response.data[0].rules_description);
                    this.tableEdiRequiredField.displayData(updatedRow, this.tableEdiRequiredField.dataSource.data, 0);
                    superOnSave(updatedRow);
                }
            };
        };

        this.tableEdiRequiredField.addRowCreateListener((event) => {
            // This fires when a new row is being added to the table
            const tableRow = event.getTableRow();
            tableRow.deleteRow = (afterDelete?: () => {}) => {
                ReflectiveDialogs.showYesNo("Are you sure you want to delete this record?", "Confirm Deletion").then(async (clickedYes) => {
                    if (clickedYes) {
                        try {
                            // After successful API call, perform the standard table row deletion
                            this.tableEdiRequiredField.deleteRow(tableRow.index);
                            this.needsSaving = true;

                            // Call the afterDelete callback if provided
                            if (afterDelete) {
                                afterDelete();
                            }
                        } catch (error) {
                            console.error("Error deleting EDI required field:", error);
                            // Handle error as needed
                        }
                    }
                });
            };

            if (tableRow.table.editLayout) {
                // Override the edit layout loading process
                const originalEditLayoutLoaded = tableRow.table.onEditLayoutLoaded;
                tableRow.table.onEditLayoutLoaded = (decorator, row) => {

                    const layout = decorator.layout;
                    if (layout instanceof EdiRequiredFieldStatus) {
                        // Set the transaction type to "R" for Reply when opened from LoadTenderReplyProfile
                        layout.transactionType = this.transactionType;
                        layout.isGroupRule = true;
                        layout.version = this.version;
                        layout.partnerId = this.partnerId;
                    }

                    // Hide Save & Add Another option in the multibutton for edit layout
                    if (decorator.multiButton && decorator.multiButton.components && decorator.multiButton.components.length > 1) {
                        decorator.multiButton.components[1].visible = false;
                    }

                    // Call the original handler if it exists
                    if (originalEditLayoutLoaded) {
                        originalEditLayoutLoaded(decorator, row);
                    }
                };
            }
        });
    }
    /** This is an event handler for the onClick event of buttonAddRequiredField. */
    buttonAddRequiredFieldOnClick(event: ClickEvent) {
        // Create new EdiRequiredFieldStatus layout
        const layoutName = "lme/datafusion/EdiRequiredFieldStatus";
        const layout = Layout.getLayout(layoutName) as EdiRequiredFieldStatus;
        layout.isGroupRule = true;
        layout.isCreationRule = true;
        layout.transactionType = this.transactionType;
        layout.version = this.version;
        layout.partnerId = this.partnerId;

        // Create the crud decorator for slideout
        const crudDecorator = new CrudDecorator({
            layout: layout,
            mode: DataSourceMode.ADD,
            headerProps: {
                showClose: true,
                showSaveAndClose: true,
                showSave: false,
                title: "Status Creation Conditional Rules - New",
                doAfterClose: async (event) => {
                        const closeEvent = event as unknown as CrudDecoratorCloseEvent;
                        if (!closeEvent.isCancel()) {
                        const dataSource = layout.mainDataSource.activeRow;
                        const dataRules = layout.tableEdiDataRule?.data?.map((row) => row.data) || [];
                        dataSource.set("data_rules", dataRules);
                        dataSource.set("version", this.version);
                        const rowToSend = dataSource?.data ? dataSource.data : dataSource;
                        const response = await Api.post("lme/datafusion/set-rules-description", { required_fields: rowToSend, version: layout.version });
                        dataSource.set("rule_description", response.data[0].rules_description);
                        this.tableEdiRequiredField.addRow(dataSource, { mode: TableRowMode.NONE }, { display: true, addToData: true });
                        this.needsSaving = true;
                    }
                }
            },
            doAfterSlideIn: async () => {
                // Set the new row values appropriately using private variables
                layout.mainDataSource?.activeRow?.set("version", this._version);
                layout.mainDataSource?.activeRow?.set("partner_id", this._partnerId);
                layout.mainDataSource?.activeRow?.set("alt_partner_id", this._altPartnerId);
                layout.mainDataSource?.activeRow?.set("group_id", this._groupId);
                layout.mainDataSource?.activeRow?.set("sequence", this.tableEdiRequiredField.data.length + 1);
                layout.transactionType = this.transactionType;
                layout.version = this.version;
                layout.tableEdiDataRule.dataSource.setRowsAndMode(DataSourceMode.NONE, []);

            }
        });

        // Open as slideout
        crudDecorator.slideIn({ speed: 200 });
    }

    /** This is an event handler for the onRowDisplay event of tableEdiRequiredField. */
    tableEdiRequiredFieldOnRowDisplay(event: TableRowDisplayEvent) {
        const row: TableRow = event.getTableRow();
    }

    /** This is an event handler for the afterTableRowMove event of tableEdiRequiredField. */
    tableEdiRequiredFieldAfterTableRowMove(event: TableRowMoveEvent) {
        // Update sequence field in the data to match the new row order
        // Get all rows in their current visual order
        const allRows = this.tableEdiRequiredField.allRows;

        // Update sequence numbers based on current visual position
        allRows.forEach((row, index) => {
            const newSequence = index + 1;
            // Only update if the sequence has actually changed to avoid unnecessary updates
            row.data.set("sequence", newSequence);
        });

        // Mark as needing save
        this.needsSaving = true;
    }

    /** This is an event handler for the onContentsChanged event of tableEdiRequiredField. */
    tableEdiRequiredFieldOnContentsChanged(event: TableContentsChangedEvent) {
        this.needsSaving = true;
    }
}


